import React, { useState, useEffect, useRef } from 'react';

import { useResizeObserver } from 'usehooks-ts';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Table, Tooltip, Flex, Modal } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { NotificationInstance } from 'antd/es/notification/interface';
import { LoadingOutlined, ReloadOutlined, CheckOutlined, QuestionCircleOutlined } from '@ant-design/icons';

import ConsignmentCard, { IBoxGroupCardItem } from '@components/consignment-card/consignment-card';

import IBaseComponentProps from '@components/base-component-props';

import { Tags, TransitWarehouseTag } from '@controls/tags';
import Toolbar from '@controls/toolbar/toolbar';

import { exception } from '@extensions/notification';

import { userLoaded } from '@store/actions';
import { useAppDispatch } from '@store/hooks';

import { ITruckFilter } from '@entities/truck-filter';
import { IConsignment } from '@entities/consignment';
import { ICountry } from '@entities/country';
import { ICity } from '@entities/city';

dayjs.extend(utc);

interface ITruckDetailsProps extends IBaseComponentProps {
    path: string;
    truckId: string;
    filter: ITruckFilter;
    countries: Array<ICountry>;
    journalId?: string;
    api: NotificationInstance;
    onConfirm?: () => void;
}

const CargoDetails = (props: ITruckDetailsProps) => {
    const { path, truckId, journalId, filter, countries, api, onConfirm, serverFetch } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();

    const [consignments, setСonsignments] = useState<Array<IConsignment>>([]);

    const [refreshRequired, setRefreshRequired] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!refreshRequired) return;

        let cleanup = false;

        setСonsignments([]);

        const fetchData = async () => {
            setLoading(true);

            let consignmentFilter = { ...filter };
            consignmentFilter.ids = [truckId];
            const promises = [
                await serverFetch(`${path}/details`, { method: 'GET', queryParams: consignmentFilter })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения деталей поставки', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setСonsignments(result[0][0] || []);

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    const onConfirmdelivery = () => {
        const boxGroupIds: Array<string> = [];
        consignments?.map((c) => {
            c.boxGroupIds?.map((id) => {
                if (id) boxGroupIds.push(id);
            });
        });

        serverFetch(`shipmentjournals/${journalId}/confirmdelivery`, { method: 'POST', bodyData: boxGroupIds })
            .then(() => {
                if (onConfirm) onConfirm();
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка подтверждения получения груза', ex, () => d(userLoaded(undefined)));
            });
    };

    let commands: Array<any> = [
        {
            label: 'Обновить',
            key: 'refresh',
            disabled: loading,
            icon: <ReloadOutlined />,
            onClick: () => {
                setRefreshRequired(true);
            },
        },
    ];

    if (journalId) {
        commands.push({
            label: 'Подтвердить получение',
            key: 'forceDelivered',
            type: 'primary',
            icon: <CheckOutlined />,
            onClick: () => {
                modal.confirm({
                    title: `Подтвердить получение груза?`,
                    okType: 'primary',
                    icon: <QuestionCircleOutlined />,
                    okText: 'ОК',
                    cancelText: 'Отмена',
                    onOk: () => {
                        onConfirmdelivery();
                    },
                });
            },
        });
    }

    const renderToolbar = () => {
        return <Toolbar commands={commands} />;
    };

    const expandedConsignments = (record: ICity) => {
        const consignments = record.consignments?.map((p) => {
            const boxGroups: any = [];
            let totalQty: number = 0;
            let totalVolume: number = 0;

            p.boxGroups?.map((b) => {
                if (!b.countryId) return;

                boxGroups[b.countryId] = b.qty;
                totalQty += b.qty || 0;
                totalVolume += b.calcVolume || b.volume || 0;
            });

            p = {
                ...p,
                ...boxGroups,
                totalQty: totalQty,
                totalVolume: totalVolume,
            };
            return p;
        });

        const columns: ColumnsType<IConsignment> = [
            {
                title: 'ID',
                width: 60,
                align: 'center',
                render: (_: any, record: IConsignment) => {
                    return (
                        <>
                            {record.consigneeCode}
                            <TransitWarehouseTag warehouseCode={record.warehouseCode} />
                        </>
                    );
                },
            },
            {
                title: 'Маркировка',
                width: 200,
                dataIndex: 'markingCode',
            },
            {
                dataIndex: 'countries',
                width: 375,
                render: (_, record) => {
                    return (
                        <Flex vertical gap='small'>
                            {countries.map((c) => {
                                const boxGroups = record.boxGroups?.filter((bg) => bg.countryId === c.id);
                                if (!boxGroups || boxGroups.length <= 0) return undefined;

                                return (
                                    <ConsignmentCard
                                        country={c}
                                        api={api}
                                        items={boxGroups as Array<IBoxGroupCardItem>}
                                        serverFetch={serverFetch}
                                    />
                                );
                            })}
                        </Flex>
                    );
                },
            },
            {
                title: 'Кол-во / шт',
                align: 'center',
                width: 120,
                dataIndex: 'totalQty',
                onCell: (record) => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
            },
            {
                title: (
                    <>
                        Объем / м<sup>3</sup>
                    </>
                ),
                align: 'center',
                dataIndex: 'totalVolume',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    return record.totalVolume?.toFixed(2);
                },
            },
            {},
        ];

        return <Table rowKey='id' size='small' columns={columns} dataSource={consignments} pagination={false} />;
    };

    const renderTable = () => {
        const cities: Array<ICity> = [];

        consignments?.map((e) => {
            let city = cities.find((c) => c.name === e.cityName && c.tag === e.tag);
            if (!city) {
                city = {
                    cityKey: `${e.cityName}#${e.tag}`,
                    name: e.cityName,
                    tag: e.tag,
                    totalQty: 0,
                    totalVolume: 0,
                    consignments: [],
                };

                cities.push(city);
            }

            e.boxGroups?.map((b) => {
                if (city) {
                    if (!city.totalQty) city.totalQty = 0;
                    if (!city.totalVolume) city.totalVolume = 0;

                    city.totalQty += b.qty || 0;
                    city.totalVolume += b.calcVolume || b.volume || 0;
                }
            });

            city.consignments?.push(e);
        });

        const columns: ColumnsType<ICity> = [
            {
                title: 'Город доставки',
                width: 633,
                onCell: () => ({
                    style: {
                        fontWeight: 700,
                    },
                }),
                render: (_, record) => {
                    return (
                        <Flex>
                            <span>{record.name}</span>
                            <Tags label={record.tag} />
                        </Flex>
                    );
                },
            },
            {
                title: 'Кол-во / шт',
                align: 'center',
                width: 120,
                dataIndex: 'totalQty',
                onCell: () => ({
                    style: {
                        background: '#fdebd2',
                        fontWeight: 700,
                    },
                }),
            },
            {
                title: (
                    <>
                        Объем / м<sup>3</sup>
                    </>
                ),
                align: 'center',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fdebd2',
                        fontWeight: 700,
                    },
                }),
                render: (_, record) => {
                    return record.totalVolume?.toFixed(2);
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='cityKey'
                size='small'
                columns={columns}
                dataSource={cities}
                pagination={false}
                expandable={{
                    columnWidth: 32,
                    expandedRowRender: expandedConsignments /* , defaultExpandedRowKeys: cityNames  */,
                }}
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                scroll={{ y: height, x: 'max-content' }}
                summary={(pageData) => {
                    let qty = 0;
                    let volumeWeight = 0;
                    let totalLinesVolumeWeight = 0;

                    pageData.forEach(({ totalQty, totalVolume }) => {
                        qty += totalQty || 0;
                        volumeWeight += totalVolume || 0;
                    });

                    return (
                        <Table.Summary fixed>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={2} align='left'>
                                    ИТОГО
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2} align='center'>
                                    {qty}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={3} align='center'>
                                    {volumeWeight?.toFixed(2)}
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    );
                }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 225px)` }}>
            {renderToolbar()}
            {renderTable()}

            {modalContextHolder}
        </div>
    );
};

export default CargoDetails;
